import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Progress as ProgressBar } from '../components/ui/progress';
import { ArrowLeft, Trophy, Fire, Lightning, Timer, Medal, Star, Crown, House, CalendarBlank, ChartLineUp, Target } from '@phosphor-icons/react';

const MILESTONE_ICONS = {
  first_workout: Lightning,
  week_warrior: Fire,
  month_master: Star,
  century: Trophy,
  marathon_minutes: Timer,
  halfway: Medal,
  finisher_365: Crown,
};

const Milestones = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/milestones`, { withCredentials: true, headers });
        setMilestones(response.data.milestones || []);
      } catch (error) {
        console.error('Failed to fetch milestones:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMilestones();
  }, [token]);

  const earnedCount = milestones.filter(m => m.earned).length;

  if (loading) {
    return (<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>);
  }

  return (
    <div className="min-h-screen bg-background pb-24 hexagon-bg" data-testid="milestones-screen">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn"><ArrowLeft weight="bold" className="w-5 h-5 text-foreground" /></button>
          <div className="flex-1"><h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Milestones</h1><p className="text-xs text-muted-foreground">{earnedCount} of {milestones.length} earned</p></div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-6 space-y-4">
        {/* Summary Card */}
        <div className="premium-card p-6 text-center">
          <div className="relative inline-block mb-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${earnedCount > 0 ? 'bg-gradient-to-br from-[#D4AF37] to-[#B8860B]' : 'bg-card border border-border'}`}>
              <Trophy weight={earnedCount > 0 ? 'fill' : 'duotone'} className={`w-10 h-10 ${earnedCount > 0 ? 'text-black' : 'text-muted-foreground'}`} />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{earnedCount} Badges Earned</h2>
          <p className="text-muted-foreground text-sm mt-1">Keep going to unlock more achievements!</p>
        </div>

        {/* Milestones Grid */}
        <div className="grid grid-cols-1 gap-4">
          {milestones.map((milestone) => {
            const Icon = MILESTONE_ICONS[milestone.id] || Medal;
            return (
              <div key={milestone.id} className={`premium-card p-5 ${milestone.earned ? 'border-[#D4AF37]/30' : ''}`} data-testid={`milestone-${milestone.id}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${milestone.earned ? 'bg-gradient-to-br from-[#D4AF37] to-[#B8860B]' : 'bg-card border border-border'}`}>
                    <Icon weight={milestone.earned ? 'fill' : 'duotone'} className={`w-7 h-7 ${milestone.earned ? 'text-black' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold truncate ${milestone.earned ? 'text-[#D4AF37]' : 'text-foreground'}`}>{milestone.title}</h3>
                      {milestone.earned && <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full">Earned</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{milestone.description}</p>
                    {!milestone.earned && (
                      <div className="mt-2">
                        <ProgressBar value={milestone.percentage} className="h-1.5" />
                        <p className="text-xs text-muted-foreground mt-1">{milestone.progress} / {milestone.threshold}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-xl border-t border-border px-6 py-3 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link to="/dashboard" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-home"><House weight="duotone" className="w-6 h-6" /><span className="text-xs">Home</span></Link>
          <Link to="/calendar" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-calendar"><CalendarBlank weight="duotone" className="w-6 h-6" /><span className="text-xs">Calendar</span></Link>
          <Link to="/progress" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-progress"><ChartLineUp weight="duotone" className="w-6 h-6" /><span className="text-xs">Progress</span></Link>
          <Link to="/goals" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-goals"><Target weight="duotone" className="w-6 h-6" /><span className="text-xs">Goals</span></Link>
          <Link to="/milestones" className="flex flex-col items-center gap-1 text-[#D4AF37]" data-testid="nav-milestones"><Medal weight="fill" className="w-6 h-6" /><span className="text-xs">Badges</span></Link>
        </div>
      </nav>
    </div>
  );
};

export default Milestones;
